// Engineered fixture data for Dave & Sarah (§13.3).
// Hand-crafted so every blend section has known, hand-checkable content and the
// compatibility score has a computed known value (79). Consumed by unit tests,
// the zip generator, and the seed script.
//
// Verified expected blend (dave, sarah):
//   bothRated = 12, bothWatched = 12, unionWatched = 30, jaccard = 0.4
//   meanGap = 16/12 = 1.3333 -> tasteTerm = 0.703704, overlapTerm = 1.0
//   score = round((0.703704*0.7 + 1.0*0.3)*100) = 79  -> "great double bill"
//   sharedCanon = 5, disputes (gap>0) = 10, max dispute = The Room (5 vs 0.5)
//   watchTonight (both watchlisted) = 3, crossRecs A->B = 3, B->A = 3

import type { CsvRow, ExportTables } from "../src/lib/distiller.ts";

interface FilmSpec {
  uri: string;
  name: string;
  year: number;
}

// --- Shared films, watched & rated by both (drive taste + disputes) ---
interface Shared extends FilmSpec {
  dave: number;
  sarah: number;
  bothWatchlist?: boolean; // not used for watched films
}

const SHARED: Shared[] = [
  { uri: "boxd.it/2a9q", name: "Fight Club", year: 1999, dave: 4.5, sarah: 4.0 },
  { uri: "boxd.it/kx1a", name: "Aftersun", year: 2022, dave: 5.0, sarah: 5.0 },
  { uri: "boxd.it/room", name: "The Room", year: 2003, dave: 5.0, sarah: 0.5 }, // max dispute
  { uri: "boxd.it/par1", name: "Parasite", year: 2019, dave: 4.5, sarah: 5.0 },
  { uri: "boxd.it/drv1", name: "Drive", year: 2011, dave: 4.0, sarah: 4.5 },
  { uri: "boxd.it/cat1", name: "Cats", year: 2019, dave: 1.0, sarah: 3.5 },
  { uri: "boxd.it/whp1", name: "Whiplash", year: 2014, dave: 5.0, sarah: 4.0 },
  { uri: "boxd.it/ten1", name: "Tenet", year: 2020, dave: 2.0, sarah: 4.5 },
  { uri: "boxd.it/mnl1", name: "Moonlight", year: 2016, dave: 4.5, sarah: 4.5 },
  { uri: "boxd.it/her1", name: "Her", year: 2013, dave: 4.0, sarah: 3.0 },
  { uri: "boxd.it/mdy1", name: "Mandy", year: 2018, dave: 3.5, sarah: 3.0 },
  { uri: "boxd.it/hrd1", name: "Hereditary", year: 2018, dave: 4.5, sarah: 2.0 },
];

// --- Dave-only watched (rated >=4 become cross-recs to Sarah) ---
const DAVE_ONLY_RATED: FilmSpec[] = [
  { uri: "boxd.it/ncr1", name: "Nightcrawler", year: 2014 }, // on Sarah's watchlist -> badge
  { uri: "boxd.it/sic1", name: "Sicario", year: 2015 },
  { uri: "boxd.it/prs1", name: "Prisoners", year: 2013 },
  { uri: "boxd.it/enm1", name: "Enemy", year: 2013 }, // rated 3.0, not a cross-rec
];
const DAVE_ONLY_RATINGS: Record<string, number> = {
  "boxd.it/ncr1": 4.5,
  "boxd.it/sic1": 4.0,
  "boxd.it/prs1": 5.0,
  "boxd.it/enm1": 3.0,
};

// --- Sarah-only watched (rated >=4 become cross-recs to Dave) ---
const SARAH_ONLY_RATED: FilmSpec[] = [
  { uri: "boxd.it/ldb1", name: "Lady Bird", year: 2017 },
  { uri: "boxd.it/ltw1", name: "Little Women", year: 2019 },
  { uri: "boxd.it/prt1", name: "Portrait of a Lady on Fire", year: 2019 },
  { uri: "boxd.it/frn1", name: "Frances Ha", year: 2012 }, // 3.5, not a cross-rec
];
const SARAH_ONLY_RATINGS: Record<string, number> = {
  "boxd.it/ldb1": 4.5,
  "boxd.it/ltw1": 5.0,
  "boxd.it/prt1": 4.5,
  "boxd.it/frn1": 3.5,
};

// --- Extra watched-unrated padding, to make Jaccard land at 0.4 ---
const DAVE_PADDING: FilmSpec[] = [
  { uri: "boxd.it/pad1", name: "Collateral", year: 2004 },
  { uri: "boxd.it/pad2", name: "Heat", year: 1995 },
  { uri: "boxd.it/pad3", name: "Zodiac", year: 2007 },
  { uri: "boxd.it/pad4", name: "The Insider", year: 1999 },
  { uri: "boxd.it/pad5", name: "Michael Clayton", year: 2007 },
  { uri: "boxd.it/pad6", name: "Nightcrawler II", year: 2099 },
];
const SARAH_PADDING: FilmSpec[] = [
  { uri: "boxd.it/spd1", name: "20th Century Women", year: 2016 },
  { uri: "boxd.it/spd2", name: "The Farewell", year: 2019 },
  { uri: "boxd.it/spd3", name: "Columbus", year: 2017 },
  { uri: "boxd.it/spd4", name: "First Cow", year: 2019 },
];

// --- Watchlists ---
const MUTUAL_WATCHLIST: FilmSpec[] = [
  { uri: "boxd.it/dun2", name: "Dune: Part Two", year: 2024 },
  { uri: "boxd.it/sub1", name: "The Substance", year: 2024 },
  { uri: "boxd.it/ano1", name: "Anora", year: 2024 },
];
const DAVE_WATCHLIST_ONLY: FilmSpec[] = [{ uri: "boxd.it/poo1", name: "Poor Things", year: 2023 }];
const SARAH_WATCHLIST_ONLY: FilmSpec[] = [
  { uri: "boxd.it/chl1", name: "Challengers", year: 2024 },
  // Sarah has Nightcrawler watchlisted -> badges Dave's cross-rec.
  { uri: "boxd.it/ncr1", name: "Nightcrawler", year: 2014 },
];

// ---- CSV row builders ------------------------------------------------------

const row = (f: FilmSpec, extra: Record<string, string> = {}): CsvRow => ({
  Date: "2026-06-15",
  Name: f.name,
  Year: String(f.year),
  "Letterboxd URI": `https://${f.uri}`,
  ...extra,
});

const ratingRow = (f: FilmSpec, rating: number): CsvRow =>
  row(f, { Rating: rating.toFixed(1) });

// Deterministic diary entries spread across a few years for the heat strip, so
// the year x month grid renders multiple rows.
const diaryRows = (films: FilmSpec[], ratings: Record<string, number>): CsvRow[] => {
  const months = [
    "2024-03", "2024-07", "2024-11",
    "2025-01", "2025-02", "2025-05", "2025-09", "2025-12",
    "2026-01", "2026-03", "2026-06",
  ];
  return films.map((f, i) => {
    const m = months[(i * 3) % months.length];
    const day = String((i % 27) + 1).padStart(2, "0");
    return row(f, {
      "Watched Date": `${m}-${day}`,
      Rating: ratings[f.uri] != null ? ratings[f.uri].toFixed(1) : "",
      Rewatch: i % 5 === 0 ? "Yes" : "",
    });
  });
};

function buildTables(
  watchedSpecs: FilmSpec[],
  ratings: Record<string, number>,
  watchlistSpecs: FilmSpec[],
): ExportTables {
  const watched: CsvRow[] = watchedSpecs.map((f) => row(f));
  const ratingsRows: CsvRow[] = watchedSpecs
    .filter((f) => ratings[f.uri] != null)
    .map((f) => ratingRow(f, ratings[f.uri]));
  const watchlist: CsvRow[] = watchlistSpecs.map((f) => row(f));
  const diary: CsvRow[] = diaryRows(watchedSpecs, ratings);
  return { watched, ratings: ratingsRows, watchlist, diary };
}

export function daveTables(): ExportTables {
  const watchedSpecs: FilmSpec[] = [
    ...SHARED,
    ...DAVE_ONLY_RATED,
    ...DAVE_PADDING,
  ];
  const ratings: Record<string, number> = {};
  for (const f of SHARED) ratings[f.uri] = f.dave;
  Object.assign(ratings, DAVE_ONLY_RATINGS);
  const watchlist: FilmSpec[] = [...MUTUAL_WATCHLIST, ...DAVE_WATCHLIST_ONLY];
  return buildTables(watchedSpecs, ratings, watchlist);
}

export function sarahTables(): ExportTables {
  const watchedSpecs: FilmSpec[] = [
    ...SHARED,
    ...SARAH_ONLY_RATED,
    ...SARAH_PADDING,
  ];
  const ratings: Record<string, number> = {};
  for (const f of SHARED) ratings[f.uri] = f.sarah;
  Object.assign(ratings, SARAH_ONLY_RATINGS);
  const watchlist: FilmSpec[] = [...MUTUAL_WATCHLIST, ...SARAH_WATCHLIST_ONLY];
  return buildTables(watchedSpecs, ratings, watchlist);
}

// Expected values, asserted by tests to lock the engineered relationship.
export const EXPECTED = {
  score: 79,
  tier: "great double bill" as const,
  bothRated: 12,
  bothWatched: 12,
  unionWatched: 30,
  sharedCanon: 6,
  disputes: 10,
  maxDispute: { uri: "boxd.it/room", ratingA: 5.0, ratingB: 0.5, gap: 4.5 },
  watchTonight: 3,
  crossRecsAToB: 3,
  crossRecsBToA: 3,
  exportedAt: "2026-07-01",
};
